import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isNil, noop } from 'lodash';

import {
  agentConversationsUrl,
  agentConversationUrl,
  agentExchangesUrl,
  agentExchangeUrl,
} from '~/components/AgentChat/routes';
import type { AgentConversation, AgentConversationSummary, AgentExchange } from '~/components/AgentChat/types';
import { ASYNC_EXCHANGE_STATUS } from '~/components/AiChat/constants/asyncChat';
import type {
  ChatActiveExchange,
  ChatController,
  ChatExchangeView,
  ChatHistory,
  ChatHistoryEntry,
  ChatPresentation,
} from '~/components/AiChat/controller/types';
import { type ExchangeTransport, useExchangeRunner } from '~/components/AiChat/hooks/useExchangeRunner';
import type { AgentConversationCreated, AgentExchangeSubmitted } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

// The query parameter that keeps the open conversation across reloads and links.
const CONVERSATION_PARAM = 'conversation';
// What a conversation with no title shows in the previous-chats list, as the chat title shows it.
const UNTITLED_CONVERSATION_TITLE = 'New Chat';

// A turn sends the user's text alone, which the runner keeps for Retry. No submit extra (the agent chat offers no
// regenerate) and no real-time token (it has no live progress).
type AgentChatTransport = ExchangeTransport<string, never, AgentExchange, AgentExchange, never>;

interface Transcript {
  rows: AgentExchange[];
  title: string | null;
  loadState: ChatController['loadState'];
  // A failed turn the conversation recorded, whose question Retry sent again: its answer takes the turn's place.
  retriedRowId: string | null;
}

// The conversation opened: the one whose turns are loaded, and the runner's reset key.
interface OpenedConversation {
  key: number;
  conversationsUrl: string;
  conversationId: string | null;
}

export interface UseAgentChatControllerParams {
  organizationId: number;
  agentKey: string;
  presentation: ChatPresentation;
}

const conversationIdIn = (search: string): string | null => new URLSearchParams(search).get(CONVERSATION_PARAM);

const withConversationId = (search: string, conversationId: string): string => {
  const params = new URLSearchParams(search);
  params.set(CONVERSATION_PARAM, conversationId);
  return `?${params.toString()}`;
};

const withoutConversationId = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(CONVERSATION_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

const untitledConversationTitle = () => UNTITLED_CONVERSATION_TITLE;
const conversationAnalyticsId = (id: string) => id;

const toHistoryEntry = (conversation: AgentConversationSummary): ChatHistoryEntry => ({
  id: conversation.id,
  title: conversation.title,
  createdAt: conversation.created_at,
});

const transcriptOf = (conversationId: string | null): Transcript => ({
  rows: [],
  title: null,
  loadState: isNil(conversationId) ? 'ready' : 'loading',
  retriedRowId: null,
});

const toChatExchangeView = (exchange: AgentExchange): ChatExchangeView => ({
  id: exchange.id,
  userInput: exchange.user_input,
  status: exchange.status,
  response: exchange.response,
  error: exchange.error,
  createdAt: exchange.created_at,
  responseTimeMs: isNil(exchange.completed_at)
    ? null
    : Date.parse(exchange.completed_at) - Date.parse(exchange.created_at),
  artifacts: exchange.artifacts,
});

// A recorded failure while Retry sends its question again: shown as the turn being answered.
const toRetriedView = (exchange: AgentExchange): ChatExchangeView => ({
  ...toChatExchangeView(exchange),
  status: ASYNC_EXCHANGE_STATUS.PROCESSING,
  error: null,
  responseTimeMs: null,
});

// The answered turn takes its row's place, or joins the end. It replaces the failed turn Retry sent it for, and any
// row still running: a retry is a new exchange, so the row it retried never resolves here.
const withAnswer = (rows: AgentExchange[], answered: AgentExchange, retriedRowId: string | null): AgentExchange[] => {
  const kept = rows.filter(
    (row) => row.id === answered.id || (row.id !== retriedRowId && row.status !== ASYNC_EXCHANGE_STATUS.PROCESSING)
  );
  return kept.some((row) => row.id === answered.id)
    ? kept.map((row) => (row.id === answered.id ? answered : row))
    : [...kept, answered];
};

/**
 * A conversation with the agent `agentKey` about an organization, as a `ChatController`. `?conversation=` names it:
 * it is loaded from there, and the first message creates one and writes its id there. Each turn runs on the exchange
 * runner, polled until it is answered or fails. It offers a new conversation (which drops `?conversation=`), renaming
 * it, and the user's previous conversations with the agent (opening one writes its id to `?conversation=`). The server
 * titles a conversation from its first message.
 */
export const useAgentChatController = ({
  organizationId,
  agentKey,
  presentation,
}: UseAgentChatControllerParams): ChatController => {
  const history = useHistory();
  const location = useLocation();
  const conversationId = conversationIdIn(location.search);
  const conversationsUrl = agentConversationsUrl(organizationId, agentKey);

  // The id the first message created, until the URL shows it.
  const [createdConversationId, setCreatedConversationId] = React.useState<string | null>(null);
  const [shown, setShown] = React.useState({ conversationsUrl, conversationId });
  const [opened, setOpened] = React.useState<OpenedConversation>({ key: 0, conversationsUrl, conversationId });
  const [transcript, setTranscript] = React.useState<Transcript>(() => transcriptOf(conversationId));
  // Bumped when the list of previous conversations changes: a message to an untitled conversation titles it, and the
  // first one creates it.
  const [listVersion, setListVersion] = React.useState(0);

  // The URL changed: another conversation is opened, unless the first message wrote the id it created there. That
  // turn belongs to the conversation already on screen, so its run and its transcript stay.
  if (shown.conversationsUrl !== conversationsUrl || shown.conversationId !== conversationId) {
    const isCreatedBySend =
      shown.conversationsUrl === conversationsUrl &&
      isNil(shown.conversationId) &&
      conversationId === createdConversationId;
    setShown({ conversationsUrl, conversationId });
    if (isCreatedBySend) {
      setCreatedConversationId(null);
    } else {
      setOpened({ key: opened.key + 1, conversationsUrl, conversationId });
      setTranscript(transcriptOf(conversationId));
    }
  }

  React.useEffect(() => {
    if (isNil(opened.conversationId)) return undefined;

    let cancelled = false;
    axios
      .get<AgentConversation>(agentConversationUrl(opened.conversationsUrl, opened.conversationId))
      .then(({ data }) => {
        if (!cancelled) {
          setTranscript({ rows: data.exchanges, title: data.title, loadState: 'ready', retriedRowId: null });
        }
      })
      .catch(async (error) => {
        if (cancelled) return;
        setTranscript((current) => ({ ...current, loadState: 'error' }));
        await reportAxiosError(error);
      });

    return () => {
      cancelled = true;
    };
  }, [opened]);

  // The transport's requests read the conversation through this, so the runner's callbacks do not change with it.
  const conversationIdRef = React.useRef(conversationId);
  React.useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // The conversation each turn sent from here was sent in: it is polled there, and answered there alone, whatever the
  // URL shows by then.
  const turnConversationIdsRef = React.useRef(new Map<string, string>());
  // The failed turn the runner holds, as the conversation recorded it: the next message moves past it, so it joins
  // the transcript then.
  const failedTurnRef = React.useRef<AgentExchange | null>(null);
  // Whether a turn belongs to a conversation: one sent from here to the one it was sent in, any other to the one
  // loaded.
  const isSentIn = React.useCallback((exchangeId: string, inConversationId: string | null) => {
    const sentIn = turnConversationIdsRef.current.get(exchangeId);
    return isNil(sentIn) || sentIn === inConversationId;
  }, []);
  // The title of the conversation on screen, for the transport, which reads it when a turn is accepted.
  const titleRef = React.useRef(transcript.title);
  React.useEffect(() => {
    titleRef.current = transcript.title;
  }, [transcript.title]);
  // A turn the server accepted for an untitled conversation titled it from that message: the list reads it again,
  // and the conversation on screen takes the title.
  const handleAccepted = React.useCallback(
    (acceptedConversationId: string) => {
      const isOnScreen = acceptedConversationId === conversationIdRef.current;
      if (isOnScreen && !isNil(titleRef.current)) return;
      setListVersion((version) => version + 1);
      if (!isOnScreen) return;
      axios
        .get<AgentConversation>(agentConversationUrl(conversationsUrl, acceptedConversationId))
        .then(({ data }) => {
          if (acceptedConversationId !== conversationIdRef.current) return;
          setTranscript((current) => (isNil(current.title) ? { ...current, title: data.title } : current));
        })
        .catch(reportAxiosError);
    },
    [conversationsUrl]
  );
  const handleAcceptedRef = React.useRef(handleAccepted);
  React.useEffect(() => {
    handleAcceptedRef.current = handleAccepted;
  }, [handleAccepted]);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const transport = React.useMemo<AgentChatTransport>(
    () => ({
      submit: async (userInput) => {
        let targetConversationId = conversationIdRef.current;
        if (isNil(targetConversationId)) {
          const { pathname } = history.location;
          const { data } = await axios.post<AgentConversationCreated>(conversationsUrl, {});
          targetConversationId = data.id;
          // Opened only where the message was sent from: a page left meanwhile, or another conversation opened on
          // it, keeps its URL.
          const isSentFromHere =
            isMountedRef.current &&
            history.location.pathname === pathname &&
            isNil(conversationIdIn(history.location.search));
          if (isSentFromHere) {
            conversationIdRef.current = data.id;
            // Before the URL shows it, so the render that reads the new URL knows this message created it.
            setCreatedConversationId(data.id);
            history.replace({ ...history.location, search: withConversationId(history.location.search, data.id) });
          }
        }
        const { data } = await axios.post<AgentExchangeSubmitted>(
          agentExchangesUrl(conversationsUrl, targetConversationId),
          { user_input: userInput }
        );
        turnConversationIdsRef.current.set(data.exchange_id, targetConversationId);
        if (isMountedRef.current) handleAcceptedRef.current(targetConversationId);
        return { exchangeId: data.exchange_id };
      },
      fetch: async (exchangeId) => {
        // A turn not sent from here is one the loaded conversation was running.
        const runConversationId = turnConversationIdsRef.current.get(exchangeId) ?? conversationIdRef.current;
        // The conversation was left while its turn ran, and the runner is dropping that run.
        if (isNil(runConversationId)) return { status: ASYNC_EXCHANGE_STATUS.PROCESSING };

        const { data } = await axios.get<AgentExchange>(
          agentExchangeUrl(conversationsUrl, runConversationId, exchangeId)
        );
        if (data.status === ASYNC_EXCHANGE_STATUS.COMPLETED) {
          return { status: ASYNC_EXCHANGE_STATUS.COMPLETED, exchange: data };
        }
        if (data.status === ASYNC_EXCHANGE_STATUS.FAILED) {
          failedTurnRef.current = data;
          return { status: ASYNC_EXCHANGE_STATUS.FAILED, error: data.error };
        }
        return { status: ASYNC_EXCHANGE_STATUS.PROCESSING };
      },
      isInFlight: (row) => row.status === ASYNC_EXCHANGE_STATUS.PROCESSING,
      inputOf: (row) => row.user_input,
    }),
    [conversationsUrl, history]
  );

  const handleResolved = React.useCallback(
    (answered: AgentExchange) => {
      if (!isSentIn(answered.id, conversationIdRef.current)) return;
      setTranscript((current) => ({
        ...current,
        rows: withAnswer(current.rows, answered, current.retriedRowId),
        retriedRowId: null,
      }));
    },
    [isSentIn]
  );

  const {
    active,
    isFetching,
    submit: submitTurn,
    retry: retryTurn,
  } = useExchangeRunner({
    transport,
    rows: transcript.rows,
    isConnected: false,
    resetKey: String(opened.key),
    onResolved: handleResolved,
    onRealTimeToken: noop,
  });

  const send = React.useCallback(
    async (userInput: string) => {
      const failedTurn = failedTurnRef.current;
      const isMovingPastFailedTurn =
        !isNil(failedTurn) &&
        active?.status === ASYNC_EXCHANGE_STATUS.FAILED &&
        active.exchangeId === failedTurn.id &&
        isSentIn(failedTurn.id, conversationIdRef.current);
      failedTurnRef.current = null;
      // A new message moves on from the failed turn: it joins the transcript, in place of the one Retry was sending
      // again. With no such turn, the one Retry was sending again shows as failed again.
      setTranscript((current) => {
        if (isMovingPastFailedTurn) {
          return { ...current, rows: withAnswer(current.rows, failedTurn, current.retriedRowId), retriedRowId: null };
        }
        return isNil(current.retriedRowId) ? current : { ...current, retriedRowId: null };
      });
      await submitTurn(userInput);
    },
    [active, isSentIn, submitTurn]
  );

  // AiMessage does not wait on Retry, and the runner has reported a re-send the server refused, so neither path
  // rejects.
  const retry = React.useCallback(
    async (exchangeId: string) => {
      const failedRow = transcript.rows.find(
        (row) => row.id === exchangeId && row.status === ASYNC_EXCHANGE_STATUS.FAILED
      );
      if (isNil(failedRow)) {
        // The turn this conversation was running failed: the runner sends what it sent again.
        await retryTurn().catch(noop);
        return;
      }
      setTranscript((current) => ({ ...current, retriedRowId: failedRow.id }));
      await submitTurn(failedRow.user_input).catch(() =>
        setTranscript((current) => ({ ...current, retriedRowId: null }))
      );
    },
    [transcript.rows, retryTurn, submitTurn]
  );

  const exchanges = React.useMemo(
    () =>
      transcript.rows.map((row) => (row.id === transcript.retriedRowId ? toRetriedView(row) : toChatExchangeView(row))),
    [transcript.rows, transcript.retriedRowId]
  );

  const activeExchange = React.useMemo<ChatActiveExchange | null>(
    () => (isNil(active) ? null : { id: active.exchangeId, status: active.status, error: active.error ?? null }),
    [active]
  );

  const analytics = React.useMemo(() => ({ conversationId, agentKey }), [conversationId, agentKey]);

  const startNewConversation = React.useCallback(() => {
    history.push({ ...history.location, search: withoutConversationId(history.location.search) });
  }, [history]);

  const listConversations = React.useCallback(async () => {
    const { data } = await axios.get<AgentConversationSummary[]>(conversationsUrl);
    return data.map(toHistoryEntry);
  }, [conversationsUrl]);

  const openConversation = React.useCallback(
    (id: string) => {
      history.push({ ...history.location, search: withConversationId(history.location.search, id) });
      return Promise.resolve();
    },
    [history]
  );

  const renameConversation = React.useCallback(
    async (id: string, title: string) => {
      await axios.patch<AgentConversationSummary>(agentConversationUrl(conversationsUrl, id), { title });
      if (id === conversationIdRef.current) {
        setTranscript((current) => ({ ...current, title }));
      }
    },
    [conversationsUrl]
  );

  // The chat title's rename: it reverts the title when this rejects.
  const rename = React.useMemo(() => {
    if (isNil(conversationId)) return undefined;
    return async (title: string) => {
      try {
        await renameConversation(conversationId, title);
      } catch (error) {
        await reportAxiosError(error);
        throw error;
      }
    };
  }, [conversationId, renameConversation]);

  const conversationHistory = React.useMemo<ChatHistory>(
    () => ({
      currentId: conversationId,
      list: listConversations,
      revision: listVersion,
      open: openConversation,
      rename: renameConversation,
      untitledTitle: untitledConversationTitle,
      analyticsIdOf: conversationAnalyticsId,
    }),
    [conversationId, listConversations, listVersion, openConversation, renameConversation]
  );

  return React.useMemo<ChatController>(
    () => ({
      conversationId,
      title: transcript.title,
      loadState: transcript.loadState,
      exchanges,
      activeExchange,
      isAnswering: isFetching,
      showWelcome: isNil(conversationId),
      send,
      retry,
      presentation,
      analytics,
      startNewConversation,
      rename,
      history: conversationHistory,
    }),
    [
      conversationId,
      transcript.title,
      transcript.loadState,
      exchanges,
      activeExchange,
      isFetching,
      send,
      retry,
      presentation,
      analytics,
      startNewConversation,
      rename,
      conversationHistory,
    ]
  );
};
